import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import { Link } from "react-router-dom";
import { useSwipeable } from "react-swipeable";
import { motion, AnimatePresence } from "framer-motion";

const TABS = [
  { id: "imageGallery", label: "Images" },
  { id: "videoGallery", label: "Videos" },
  { id: "audioGallery", label: "Audios" },
  { id: "files", label: "Files" },
];

const INITIAL_VISIBLE = {
  imageGallery: 24,
  videoGallery: 12,
  audioGallery: 20,
  files: 20,
};

const LOAD_MORE = {
  imageGallery: 16,
  videoGallery: 8,
  audioGallery: 10,
  files: 10,
};

const MyGallery = () => {
  const authState = useSelector((state) => state.authReducer);
  const [fetchData, { loading }] = useFetch();

  const [files, setFiles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [activeTab, setActiveTab] = useState("imageGallery");
  const [visibleCounts, setVisibleCounts] = useState(INITIAL_VISIBLE);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const [menuOpenId, setMenuOpenId] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalItems, setModalItems] = useState([]);
  const [modalIndex, setModalIndex] = useState(0);

  const endOfListRef = useRef(null);

  // ----------------- Helpers -----------------
  const buildPath = (fp) => `/api/downloads${fp.replace(/.*uploads/, "")}`;

  const getCategory = (item) =>
    item.folder || item.category || item.type || "General";

  const filterBySearchAndCategory = (items) => {
    return items.filter((item) => {
      const title = item.title || "";
      const cat = getCategory(item);
      const matchSearch =
        !searchTerm ||
        title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchCategory =
        selectedCategory === "All" || cat === selectedCategory;
      return matchSearch && matchCategory;
    });
  };

  const currentData = useMemo(() => {
    switch (activeTab) {
      case "imageGallery":
        return filterBySearchAndCategory(files);
      case "videoGallery":
        return filterBySearchAndCategory(videos);
      case "audioGallery":
        return filterBySearchAndCategory(audios);
      case "files":
        return filterBySearchAndCategory(documents);
      default:
        return [];
    }
  }, [activeTab, files, videos, audios, documents, searchTerm, selectedCategory]);

  const currentVisibleCount = visibleCounts[activeTab] || 0;
  const slicedData = currentData.slice(0, currentVisibleCount);
  const hasMore = currentVisibleCount < currentData.length;

  const categories = useMemo(() => {
    const src =
      activeTab === "imageGallery"
        ? files
        : activeTab === "videoGallery"
        ? videos
        : activeTab === "audioGallery"
        ? audios
        : documents;
    const set = new Set();
    src.forEach((item) => set.add(getCategory(item)));
    return ["All", ...Array.from(set)];
  }, [activeTab, files, videos, audios, documents]);

  // ----------------- Fetch data -----------------
  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        const uid = authState?.user?._id;
        const token = authState?.token;
        if (!uid || !token) return;

        const req = (url) =>
          fetchData(
            {
              url,
              method: "post",
              headers: { Authorization: token },
              data: { userId: uid },
            },
            { showSuccessToast: false }
          );

        const img = await req("/uploads/getusersimagegallery");
        if (img?.status) setFiles(img.myimages || []);

        const vid = await req("/uploads/getusersvideos");
        if (vid?.status) setVideos(vid.myvideos || []);

        const aud = await req("/uploads/getusersaudios");
        if (aud?.status) setAudios(aud.myaudios || []);

        const doc = await req("/uploads/getusersfiles");
        if (doc?.status) setDocuments(doc.myfiles || []);
      } catch (err) {
        console.error("Error fetching gallery data:", err);
      }
    };

    fetchGalleryData();
  }, [authState, fetchData]);

  // ----------------- Infinite scroll -----------------
  useEffect(() => {
    if (!hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCounts((prev) => ({
            ...prev,
            [activeTab]: Math.min(
              prev[activeTab] + (LOAD_MORE[activeTab] || 10),
              currentData.length
            ),
          }));
        }
      },
      { threshold: 1 }
    );

    const el = endOfListRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, [activeTab, currentData.length, hasMore]);

  // ----------------- Swipe tabs -----------------
  const activeTabIndex = TABS.findIndex((t) => t.id === activeTab);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (activeTabIndex < TABS.length - 1) {
        setActiveTab(TABS[activeTabIndex + 1].id);
        setSelectedCategory("All");
        setVisibleCounts((prev) => ({
          ...prev,
          [TABS[activeTabIndex + 1].id]: INITIAL_VISIBLE[
            TABS[activeTabIndex + 1].id
          ],
        }));
      }
    },
    onSwipedRight: () => {
      if (activeTabIndex > 0) {
        setActiveTab(TABS[activeTabIndex - 1].id);
        setSelectedCategory("All");
        setVisibleCounts((prev) => ({
          ...prev,
          [TABS[activeTabIndex - 1].id]: INITIAL_VISIBLE[
            TABS[activeTabIndex - 1].id
          ],
        }));
      }
    },
    trackMouse: true,
  });

  // ----------------- Modal Preview -----------------
  const openModal = (startItem) => {
    setModalItems(currentData);
    const idx = currentData.findIndex((i) => i._id === startItem._id);
    setModalIndex(idx >= 0 ? idx : 0);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const showPrev = () =>
    setModalIndex((prev) =>
      prev === 0 ? modalItems.length - 1 : prev - 1
    );
  const showNext = () =>
    setModalIndex((prev) =>
      prev === modalItems.length - 1 ? 0 : prev + 1
    );

  const modalSwipeHandlers = useSwipeable({
    onSwipedLeft: showNext,
    onSwipedRight: showPrev,
    trackMouse: true,
  });

  const currentModalItem =
    modalOpen && modalItems.length > 0 ? modalItems[modalIndex] : null;

  // ----------------- 3-dot menu actions -----------------
  const handleCopyLink = (item) => {
    const link = window.location.origin + buildPath(item.filePath);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link);
    }
    setMenuOpenId(null);
  };

  // ⚠️ These are placeholders – plug into your own APIs if needed.
  const handleDelete = async (item) => {
    try {
      // Example: update with your delete endpoint
      // await fetchData({ url: "/uploads/delete", method: "post", data: { id: item._id } });
      // console.log("Delete clicked for", item._id);
      // Optimistic UI update (remove item from local state)
      const updater = (arr) => arr.filter((x) => x._id !== item._id);
      setFiles((prev) => updater(prev));
      setVideos((prev) => updater(prev));
      setAudios((prev) => updater(prev));
      setDocuments((prev) => updater(prev));
    } catch (err) {
      console.error("Delete error", err);
    } finally {
      setMenuOpenId(null);
    }
  };

  const handleRename = async (item) => {
    const newTitle = prompt("Enter new name", item.title || "");
    if (!newTitle || newTitle === item.title) return;
    try {
      // Example: update with your rename endpoint
      // await fetchData({ url: "/uploads/rename", method: "post", data: { id: item._id, title: newTitle }});
      const updater = (arr) =>
        arr.map((x) => (x._id === item._id ? { ...x, title: newTitle } : x));

      setFiles((prev) => updater(prev));
      setVideos((prev) => updater(prev));
      setAudios((prev) => updater(prev));
      setDocuments((prev) => updater(prev));
    } catch (err) {
      console.error("Rename error", err);
    } finally {
      setMenuOpenId(null);
    }
  };

  // ----------------- Upload FAB -----------------
  const uploadInputRef = useRef(null);

  const handleUploadClick = () => {
    if (uploadInputRef.current) uploadInputRef.current.click();
  };

  const handleUploadChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // ⚠️ Plug this to your upload API
      // const formData = new FormData();
      // formData.append("file", file);
      // formData.append("userId", authState.user._id);
      // const res = await fetchData({ url: "/uploads/upload", method: "post", data: formData });
      // console.log("Upload selected file:", file.name);
    } catch (err) {
      console.error("Upload error", err);
    } finally {
      e.target.value = "";
    }
  };

  // ----------------- Render card helpers -----------------
  const renderImageCard = (item) => (
    <motion.div
      key={item._id}
      layout
      whileHover={{ scale: 1.03 }}
      className="relative rounded-lg overflow-hidden border border-blue-700 cursor-pointer bg-black/40"
      onClick={() => openModal(item)}
    >
      <img
        src={buildPath(item.filePath)}
        alt={item.title}
        className="w-full h-full object-cover"
      />
      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-xs px-2 py-1 text-blue-200 truncate">
        {item.title || "Untitled"}
      </div>
      {renderMenuButton(item)}
    </motion.div>
  );

  const renderVideoCard = (item) => (
    <motion.div
      key={item._id}
      layout
      whileHover={{ scale: 1.02 }}
      className="relative rounded-lg overflow-hidden border border-blue-700 bg-black/40"
      onClick={() => openModal(item)}
    >
      <video
        src={buildPath(item.filePath)}
        className="w-full h-32 sm:h-40 object-cover"
      />
      <div className="px-2 py-1 text-xs text-blue-200 truncate">
        {item.title || "Video"}
      </div>
      {renderMenuButton(item)}
    </motion.div>
  );

  const renderAudioCard = (item) => (
    <motion.div
      key={item._id}
      layout
      whileHover={{ scale: 1.01 }}
      className="relative rounded-lg border border-blue-700 bg-gray-900/80 p-3"
    >
      <div
        className="flex justify-between items-center mb-2"
        onClick={() => openModal(item)}
      >
        <p className="text-sm text-blue-200 truncate">
          🎧 {item.title || "Audio"}
        </p>
      </div>
      <audio src={buildPath(item.filePath)} controls className="w-full" />
      {renderMenuButton(item)}
    </motion.div>
  );

  const renderFileCard = (item) => (
    <motion.div
      key={item._id}
      layout
      whileHover={{ scale: 1.01 }}
      className="relative rounded-lg border border-blue-700 bg-gray-900/80 p-3"
    >
      <p className="text-sm text-blue-200 truncate mb-1">
        📄 {item.title || "File"}
      </p>
      <p className="text-xs text-gray-400 mb-2">{getCategory(item)}</p>
      <div className="flex gap-2">
        <a
          href={buildPath(item.filePath)}
          download
          onClick={(e) => e.stopPropagation()}
          className="text-xs bg-blue-600 px-3 py-1 rounded text-white"
        >
          Download
        </a>
        <button
          onClick={() => handleCopyLink(item)}
          className="text-xs border border-blue-500 px-3 py-1 rounded text-blue-300"
        >
          Copy Link
        </button>
      </div>
      {renderMenuButton(item)}
    </motion.div>
  );

  const renderMenuButton = (item) => (
    <div
      className="absolute top-1 right-1"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className="w-7 h-7 rounded-full bg-black/70 flex items-center justify-center text-blue-200 text-xs border border-blue-500"
        onClick={() =>
          setMenuOpenId((prev) => (prev === item._id ? null : item._id))
        }
      >
        ⋮
      </button>
      <AnimatePresence>
        {menuOpenId === item._id && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="mt-1 right-0 bg-black border border-blue-600 rounded-md shadow-lg text-xs text-blue-100 w-32 py-1 absolute z-20"
          >
            <button
              className="px-3 py-1 w-full text-left hover:bg-blue-600/30"
              onClick={() => openModal(item)}
            >
              View
            </button>
            <button
              className="px-3 py-1 w-full text-left hover:bg-blue-600/30"
              onClick={() => handleRename(item)}
            >
              Rename
            </button>
            <button
              className="px-3 py-1 w-full text-left hover:bg-blue-600/30"
              onClick={() => handleCopyLink(item)}
            >
              Copy link
            </button>
            <button
              className="px-3 py-1 w-full text-left text-red-300 hover:bg-red-600/30"
              onClick={() => handleDelete(item)}
            >
              Delete
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // ----------------- Main render -----------------
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-blue-400">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-blue-300 flex flex-col">
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="border-b border-blue-500 px-4 py-3"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <span>📤</span>
              <span>My TCloud Gallery</span>
            </h1>
            <p className="text-xs text-blue-400">
              Your personal multi-format cloud gallery
            </p>
          </div>
        </div>
      </motion.header>

      {/* Search + Filters + Tabs */}
      <div className="px-3 pt-3 pb-2 space-y-3">
        {/* Search */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search by title..."
            className="flex-1 bg-gray-900 text-blue-100 text-sm px-3 py-2 rounded-md border border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`w-1/4 px-1 py-1 text-xs rounded-md border whitespace-nowrap ${
                selectedCategory === cat
                  ? "bg-blue-600 text-white border-blue-400"
                  : "border-blue-600 text-blue-300"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto gap-2">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSelectedCategory("All");
                setVisibleCounts((prev) => ({
                  ...prev,
                  [tab.id]: INITIAL_VISIBLE[tab.id],
                }));
              }}
              className={`px-4 py-2 rounded-lg text-xs sm:text-sm border whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-blue-600 text-white border-blue-400"
                  : "border-blue-600 text-blue-400"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content zone */}
      <div
        className="flex-1 px-3 pb-20 pt-1 overflow-y-auto"
        {...swipeHandlers}
      >
        <AnimatePresence mode="wait">
          {activeTab === "imageGallery" && (
            <motion.div
              key="images"
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 15 }}
              className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2"
            >
              {slicedData.map((item) => renderImageCard(item))}
            </motion.div>
          )}

          {activeTab === "videoGallery" && (
            <motion.div
              key="videos"
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 15 }}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
            >
              {slicedData.map((item) => renderVideoCard(item))}
            </motion.div>
          )}

          {activeTab === "audioGallery" && (
            <motion.div
              key="audios"
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 15 }}
              className="space-y-3"
            >
              {slicedData.map((item) => renderAudioCard(item))}
            </motion.div>
          )}

          {activeTab === "files" && (
            <motion.div
              key="files"
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 15 }}
              className="space-y-3"
            >
              {slicedData.map((item) => renderFileCard(item))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Infinite scroll sentinel */}
        {hasMore && (
          <div ref={endOfListRef} className="h-10 flex items-center justify-center">
            <span className="text-xs text-blue-500">
              Loading more...
            </span>
          </div>
        )}

        {!loading && currentData.length === 0 && (
          <div className="text-center text-sm text-blue-400 mt-10">
            Nothing found for this filter.
          </div>
        )}
      </div>

      {/* Upload FAB */}
      <button
        onClick={handleUploadClick}
        className="fixed bottom-16 right-4 w-12 h-12 rounded-full bg-blue-600 shadow-lg flex items-center justify-center text-2xl text-white border border-blue-300"
      >
        +
      </button>
      <input
        type="file"
        ref={uploadInputRef}
        className="hidden"
        onChange={handleUploadChange}
      />

      {/* Footer */}
      <footer className="mt-auto border-t border-blue-500 text-gray-400 py-3 bg-black">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3 text-xs sm:text-sm">
          <div>© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4">
            <Link to="/about-us" className="hover:text-blue-300">
              About
            </Link>
            <Link to="/privacy-policy" className="hover:text-blue-300">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-blue-300">
              Terms of Use
            </Link>
            <Link to="/contact-us" className="hover:text-blue-300">
              Contact
            </Link>
          </div>
        </div>
      </footer>

      {/* Modal Preview */}
      <AnimatePresence>
        {modalOpen && currentModalItem && (
          <motion.div
            className="fixed inset-0 bg-black/90 z-40 flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Top bar */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-blue-600 text-blue-200 text-sm">
              <div className="truncate">
                {currentModalItem.title || "Preview"}
              </div>
              <button
                onClick={closeModal}
                className="text-lg leading-none px-2"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div
              className="flex-1 flex items-center justify-center px-3"
              {...modalSwipeHandlers}
            >
              <div className="max-w-4xl w-full flex items-center justify-center">
                {/* Left arrow */}
                <button
                  onClick={showPrev}
                  className="hidden sm:flex w-8 h-8 rounded-full border border-blue-500 items-center justify-center text-blue-200 mr-2"
                >
                  ‹
                </button>

                <div className="flex-1 flex items-center justify-center">
                  {activeTab === "imageGallery" && (
                    <img
                      src={buildPath(currentModalItem.filePath)}
                      alt={currentModalItem.title}
                      className="max-h-[70vh] max-w-full object-contain rounded-lg border border-blue-700"
                    />
                  )}
                  {activeTab === "videoGallery" && (
                    <video
                      src={buildPath(currentModalItem.filePath)}
                      controls
                      className="max-h-[70vh] max-w-full rounded-lg border border-blue-700"
                    />
                  )}
                  {activeTab === "audioGallery" && (
                    <div className="w-full max-w-xl bg-gray-900 border border-blue-700 rounded-lg p-4">
                      <p className="text-sm text-blue-200 mb-2">
                        🎧 {currentModalItem.title || "Audio"}
                      </p>
                      <audio
                        src={buildPath(currentModalItem.filePath)}
                        controls
                        className="w-full"
                      />
                    </div>
                  )}
                  {activeTab === "files" && (
                    <div className="w-full max-w-xl bg-gray-900 border border-blue-700 rounded-lg p-4 text-sm text-blue-100">
                      <p className="mb-2">
                        📄 {currentModalItem.title || "File"}
                      </p>
                      <a
                        href={buildPath(currentModalItem.filePath)}
                        download
                        className="inline-block mt-1 bg-blue-600 text-white text-xs px-3 py-1 rounded"
                      >
                        Download
                      </a>
                    </div>
                  )}
                </div>

                {/* Right arrow */}
                <button
                  onClick={showNext}
                  className="hidden sm:flex w-8 h-8 rounded-full border border-blue-500 items-center justify-center text-blue-200 ml-2"
                >
                  ›
                </button>
              </div>
            </div>

            {/* Bottom bar */}
            <div className="px-4 py-2 border-t border-blue-600 text-xs text-blue-300 flex justify-between items-center">
              <div>
                {modalIndex + 1} / {modalItems.length}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleCopyLink(currentModalItem)}
                  className="border border-blue-500 px-3 py-1 rounded"
                >
                  Copy link
                </button>
                <a
                  href={buildPath(currentModalItem.filePath)}
                  download
                  className="bg-blue-600 px-3 py-1 rounded text-white"
                >
                  Download
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyGallery;
